import type {
  BillingHistoryItem,
  CheckoutStatusResponse,
  PlansResponse,
  StripeCheckoutResponse,
  StripePortalResponse,
} from './types';
import type { ApiDomainDeps } from './domainDeps';

type BillingErrorBody = {
  message?: string;
  error?: string;
};

const toBillingErrorMessage = (body: unknown): string => {
  const parsed = (body ?? {}) as BillingErrorBody;
  return parsed.message ?? parsed.error ?? 'No subscription found. Subscribe first.';
};

export function createBillingDomain(deps: ApiDomainDeps) {
  return {
    async createCheckoutSession(): Promise<StripeCheckoutResponse> {
      return deps.request<StripeCheckoutResponse>('/api/stripe/create-checkout-session', {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },

    async createPortalSession(): Promise<StripePortalResponse> {
      const url = `${deps.apiBaseUrl}/api/stripe/create-portal-session`;
      const authHeader = await deps.tokenManager.getAuthHeader();
      let response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({}),
      });
      if (response.status === 401) {
        return deps.tokenManager.handle401AndRetry(async () => {
          const newAuth = await deps.tokenManager.getAuthHeader();
          const retryRes = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...newAuth },
            body: JSON.stringify({}),
          });
          if (retryRes.status === 400) {
            const data = await retryRes.json().catch(() => ({} as BillingErrorBody));
            throw new Error(toBillingErrorMessage(data));
          }
          if (!retryRes.ok) throw new Error(`API request failed: ${retryRes.statusText}`);
          return retryRes.json();
        });
      }
      if (response.status === 400) {
        const data = await response.json().catch(() => ({} as BillingErrorBody));
        throw new Error(toBillingErrorMessage(data));
      }
      if (!response.ok) {
        throw new Error(`API request failed: ${response.statusText}`);
      }
      return response.json();
    },

    async getCheckoutStatus(sessionId: string): Promise<CheckoutStatusResponse> {
      const encoded = encodeURIComponent(sessionId);
      return deps.request<CheckoutStatusResponse>(`/api/billing/checkout-status?session_id=${encoded}`);
    },

    async getBillingHistory(limit: number = 50): Promise<BillingHistoryItem[]> {
      const limitClamped = Math.min(Math.max(1, limit), 100);
      return deps.request<BillingHistoryItem[]>(`/api/billing/history?limit=${limitClamped}`);
    },

    async getPlans(): Promise<PlansResponse> {
      return deps.request<PlansResponse>('/api/plans');
    },
  };
}
